// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import styles from '@app/features/table_game_leaderboard/components/LeaderboardPrivacySetting.module.css';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const SHOW_ME_DESCRIPTOR = msg({
	message: 'Show me on the {productName}-wide leaderboards',
	comment:
		'Privacy > Table games: switch label. On, the user’s league (and in the Legend league their place and RR) shows on the leaderboards of all users and on their profile.',
});
const DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Your league in ranked table games shows on the leaderboards and on your profile; in the Legend league, your place and RR too. Community weekly boards are always visible to their members.',
	comment: 'Privacy > Table games: explains what the leaderboard switch shows and what it does not cover.',
});
const AGE_DESCRIPTOR = msg({
	message: 'Available from age 16.',
	comment:
		'Privacy > Table games: why the leaderboard switch is off and locked for accounts under 16 or without a date of birth.',
});

// The server never lists an account under 16 or without a date of birth, whatever this setting says.
export const LeaderboardPrivacySetting = observer(function LeaderboardPrivacySetting() {
	const {i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const allowed = Users.currentUser?.publicProfileAllowed === true;
	const change = async (show: boolean) => {
		setBusy(true);
		try {
			await UserSettingsCommands.update({tableGameLeaderboardHidden: !show});
		} catch {
			ToastCommands.error(i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR));
		} finally {
			setBusy(false);
		}
	};
	return (
		<div className={styles.container} data-flx="table-game-leaderboard.privacy-setting.container">
			<Switch
				label={i18n._(SHOW_ME_DESCRIPTOR, {productName: PRODUCT_NAME})}
				value={allowed && !UserSettings.tableGameLeaderboardHidden}
				disabled={!allowed || busy}
				onChange={(show) => void change(show)}
				data-flx="table-game-leaderboard.privacy-setting.switch"
			/>
			<p className={styles.hint} data-flx="table-game-leaderboard.privacy-setting.hint">
				{i18n._(allowed ? DESCRIPTION_DESCRIPTOR : AGE_DESCRIPTOR)}
			</p>
		</div>
	);
});
